import * as React from "react";
import { useContext } from "react";
import { Stack } from "../../../components/ui/stack";
import { UserContext } from "../../../crud/user/context";
import {
  AverageWorkerCpuChart,
  CombinedCpuChart,
  SingleNodeCpuChart,
} from "./charts/AverageWorkerCpuChart";
import {
  CombinedMemoryChart,
  SingleMachineMemoryChart,
  WorkerMemoryChart,
} from "./charts/WorkerMemoryChart";
import {
  CombinedNetChart,
  SingleNodeNetChart,
  WorkerNetChart,
} from "./charts/WorkerNetChart";
import {
  CombinedDiskChart,
  SingleNodeDiskChart,
  WorkerDiskChart,
} from "./charts/WorkerDiskChart";
import { GilChart, MiscMetricChart } from "./charts/MiscMetricChart";
import { ChartProps, ClusterUseCase } from "./charts/types";
import {
  ClusterGpuMemChart,
  ClusterGpuUtilChart,
  CombinedGpuMemChart,
  CombinedGpuUtilChart,
  HostGpuMemChart,
  HostGpuUtilChart,
} from "./charts/GpuCharts";
import { ClusterContext } from "../context";
import { getDefaultTimeRange } from "./utils";
import { InstanceLogsView } from "./LogsView";

export const MetricsView = (props: ChartProps): React.ReactElement => {
  const { user } = useContext(UserContext);
  return (
    <Stack className="w-full h-full mb-12" spacing={6}>
      {props.clusterUseCase === ClusterUseCase.singleMachine ? (
        // show "host" (scheduler) metrics
        <>
          <SingleNodeCpuChart {...props} />
          <SingleMachineMemoryChart {...props} />
          {props.hasGpu ? (
            <>
              <HostGpuUtilChart {...props} />
              <HostGpuMemChart {...props} />
            </>
          ) : (
            <></>
          )}
          <SingleNodeNetChart {...props} />
          <SingleNodeDiskChart {...props} />
        </>
      ) : props.clusterUseCase === ClusterUseCase.workerOnSchedulerCluster ? (
        // show combined metrics (@coiled.function clusters)
        <>
          <CombinedCpuChart {...props} />
          <CombinedMemoryChart {...props} />
          {props.hasGpu ? (
            <>
              <CombinedGpuUtilChart {...props} />
              <CombinedGpuMemChart {...props} />
            </>
          ) : (
            <></>
          )}
          <CombinedDiskChart {...props} />
          <CombinedNetChart {...props} />
        </>
      ) : (
        // normal clusters show scheduler and worker metrics separately
        <>
          <AverageWorkerCpuChart {...props} />
          <WorkerMemoryChart {...props} />
          {props.hasGpu ? (
            <>
              <ClusterGpuUtilChart {...props} />
              <ClusterGpuMemChart {...props} />
            </>
          ) : (
            <></>
          )}
          <WorkerDiskChart {...props} />
          <WorkerNetChart {...props} />
        </>
      )}
      <GilChart {...props} />
      {user.isStaff && <MiscMetricChart {...props} />}
    </Stack>
  );
};

export const CurrentSingleVmMetricsView = (props: {
  instanceId: number;
}): React.ReactElement => {
  const { clusterId, cluster } = useContext(ClusterContext);

  // TODO use time range for the specific instance, not the whole cluster
  const timeRange = getDefaultTimeRange(cluster);

  const chartProps: ChartProps = {
    clusterId,
    timeRange,
    defaultTimeRange: timeRange,
    instanceIds: [props.instanceId],
    refAreaLeft: 0,
    refAreaRight: 0,
    setRefAreaLeft: () => {},
    setRefAreaRight: () => {},
    setTimeRange: () => {},
  };

  return (
    <Stack className="w-full h-full">
      <SingleNodeCpuChart {...chartProps} />
      <SingleMachineMemoryChart {...chartProps} />
      <SingleNodeNetChart {...chartProps} />
      <SingleNodeDiskChart {...chartProps} />
      {cluster && (
        <InstanceLogsView
          clusterId={cluster.id}
          cluster={cluster}
          instanceId={props.instanceId}
          timeRange={timeRange}
        />
      )}
    </Stack>
  );
};
